import React, { useState, useCallback, useRef } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Upload, X, Check, AlertCircle, ImageIcon, FolderUp } from 'lucide-react';

function matchFileName(fileName, menuItems) {
  const base = fileName.replace(/\.[^.]+$/, '').toLowerCase().replace(/[_\-]/g, ' ').trim();
  let best = null, bestScore = 0;
  for (const item of menuItems) {
    const name = item.name.toLowerCase();
    if (base === name) return item;
    if (name.includes(base) || base.includes(name)) {
      const score = name === base ? 100 : base.includes(name) ? name.length : name.length * 0.8;
      if (score > bestScore) { bestScore = score; best = item; }
    }
  }
  return bestScore > 3 ? best : null;
}

export default function BulkUpload({ open, onClose, menuItems, onComplete }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const dropRef = useRef(null);
  const fileRef = useRef(null);

  const processFiles = useCallback((fileList) => {
    const newFiles = Array.from(fileList)
      .filter(f => f.type.startsWith('image/'))
      .map(f => {
        const matched = matchFileName(f.name, menuItems);
        return { file: f, preview: URL.createObjectURL(f), matched, manualId: matched?.id || '' };
      });
    setFiles(prev => [...prev, ...newFiles]);
  }, [menuItems]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    processFiles(e.dataTransfer.files);
  }, [processFiles]);

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

  const removeFile = (idx) => {
    setFiles(prev => {
      const f = prev[idx];
      URL.revokeObjectURL(f.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const setManualMatch = (idx, itemId) => {
    setFiles(prev => prev.map((f, i) => i === idx ? { ...f, manualId: itemId, matched: menuItems.find(m => m.id === itemId) } : f));
  };

  const uploadAll = async () => {
    const valid = files.filter(f => f.manualId);
    if (!valid.length) { toast.error('No files matched to menu items'); return; }
    setUploading(true);
    let done = 0, failed = 0;
    for (let i = 0; i < valid.length; i++) {
      const f = valid[i];
      setProgress(p => ({ ...p, [i]: 'uploading' }));
      try {
        const formData = new FormData();
        formData.append('file', f.file);
        await api.post(`/menu/${f.manualId}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        setProgress(p => ({ ...p, [i]: 'done' }));
        done++;
      } catch {
        setProgress(p => ({ ...p, [i]: 'error' }));
        failed++;
      }
    }
    setUploading(false);
    toast.success(`Uploaded ${done} image${done !== 1 ? 's' : ''}${failed ? `, ${failed} failed` : ''}`);
    if (done > 0) { onComplete(); }
  };

  const handleClose = () => {
    files.forEach(f => URL.revokeObjectURL(f.preview));
    setFiles([]);
    setProgress({});
    onClose();
  };

  const matchedCount = files.filter(f => f.manualId).length;
  const unmatchedCount = files.filter(f => !f.manualId).length;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) handleClose(); }}>
      <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-2xl max-h-[calc(100dvh-2rem)] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-serif text-white flex items-center gap-2">
            <FolderUp className="w-5 h-5 text-gold" /> Bulk Image Upload
          </DialogTitle>
        </DialogHeader>

        {files.length === 0 ? (
          <div
            ref={dropRef}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-white/10 hover:border-gold/40 rounded-sm p-12 flex flex-col items-center gap-4 cursor-pointer transition-colors"
            data-testid="bulk-upload-dropzone"
          >
            <Upload className="w-10 h-10 text-zinc-600" />
            <div className="text-center">
              <p className="text-white font-sans text-sm">Drag & drop images here</p>
              <p className="text-zinc-500 font-sans text-xs mt-1">or click to browse files</p>
              <p className="text-zinc-600 font-sans text-xs mt-3">Name files like the menu item (e.g. "Chicken Tikka.jpg") for auto-matching</p>
            </div>
            <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={e => processFiles(e.target.files)} data-testid="bulk-upload-file-input" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between py-2 border-b border-white/5">
              <div className="flex gap-3 text-xs font-sans">
                <span className="text-zinc-400">{files.length} file{files.length !== 1 ? 's' : ''}</span>
                <span className="text-emerald-400">{matchedCount} matched</span>
                {unmatchedCount > 0 && <span className="text-amber-400">{unmatchedCount} unmatched</span>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => fileRef.current?.click()} className="h-7 text-zinc-400 hover:text-white font-sans text-xs" data-testid="bulk-upload-add-more">
                  <Plus className="w-3 h-3 mr-1" /> Add More
                </Button>
                <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={e => processFiles(e.target.files)} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 py-2 max-h-[50vh]">
              {files.map((f, idx) => (
                <div key={`${f.file.name}-${idx}`} className={`flex items-center gap-3 p-2 rounded-sm border ${f.manualId ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-amber-500/20 bg-amber-500/5'}`} data-testid={`bulk-file-${idx}`}>
                  <img src={f.preview} alt="" className="w-12 h-12 rounded-sm object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-sans text-xs truncate">{f.file.name}</p>
                    {f.matched ? (
                      <p className="text-emerald-400 font-sans text-xs flex items-center gap-1 mt-0.5">
                        <Check className="w-3 h-3" /> {f.matched.name}
                      </p>
                    ) : (
                      <p className="text-amber-400 font-sans text-xs flex items-center gap-1 mt-0.5">
                        <AlertCircle className="w-3 h-3" /> No match
                      </p>
                    )}
                  </div>
                  <Select value={f.manualId} onValueChange={v => setManualMatch(idx, v)}>
                    <SelectTrigger className="w-40 h-8 bg-transparent border border-white/10 text-white font-sans text-xs" data-testid={`bulk-match-select-${idx}`}>
                      <SelectValue placeholder="Assign item..." />
                    </SelectTrigger>
                    <SelectContent className="bg-bg-secondary border border-white/10 max-h-48">
                      {menuItems.map(m => (
                        <SelectItem key={m.id} value={m.id} className="text-xs">{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {progress[idx] === 'uploading' && <div className="w-4 h-4 border-2 border-gold border-t-transparent rounded-full animate-spin flex-shrink-0" />}
                  {progress[idx] === 'done' && <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                  {progress[idx] === 'error' && <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
                  {!progress[idx] && (
                    <button onClick={() => removeFile(idx)} className="text-zinc-500 hover:text-red-400 flex-shrink-0" data-testid={`bulk-remove-${idx}`}>
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
              <Button variant="ghost" onClick={handleClose} className="text-zinc-400 hover:text-white font-sans text-xs" data-testid="bulk-upload-cancel">Cancel</Button>
              <Button onClick={uploadAll} disabled={uploading || matchedCount === 0} className="bg-gold text-black font-bold rounded-sm font-sans text-xs" data-testid="bulk-upload-submit">
                {uploading ? (
                  <><div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" /> Uploading...</>
                ) : (
                  <><Upload className="w-3 h-3 mr-1" /> Upload {matchedCount} Image{matchedCount !== 1 ? 's' : ''}</>
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Need Plus icon
const Plus = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M5 12h14"/><path d="M12 5v14"/>
  </svg>
);
